import React, { useState, useEffect } from 'react';
import api from '../../../services/api';

const CustomRoles = () => {
  const [roles, setRoles] = useState([]);
  const [newRole, setNewRole] = useState({ name: '', baseAccess: 'Employee' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchRoles = async () => {
    try {
      const res = await api.get('/custom-roles');
      setRoles(res.data);
    } catch (err) {
      console.error('Failed to fetch roles');
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newRole.name) return;
    setLoading(true);
    setError('');
    try {
      await api.post('/custom-roles', newRole);
      setNewRole({ name: '', baseAccess: 'Employee' });
      fetchRoles();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add role');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this custom role? Employees with this role will keep their current title, but you will not be able to assign it to new employees unless you recreate it.')) {
      try {
        await api.delete(`/custom-roles/${id}`);
        fetchRoles();
      } catch (err) {
        console.error('Failed to delete role');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 text-blue-800 p-4 rounded-xl text-sm border border-blue-100">
        <strong>Important:</strong> Custom roles are for organizational purposes (like adding "Contractor" or "Part-Time"). They automatically receive the base access level of "Employee". For security reasons, you cannot change or delete the core system roles (Super Admin, Admin, HR, Employee).
      </div>
      
      <form onSubmit={handleSubmit} className="flex gap-4 items-start">
        <div className="flex-1">
          <input
            type="text"
            placeholder="New Role Name (e.g. Contractor)"
            value={newRole.name}
            onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
            className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !newRole.name}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400"
        >
          {loading ? 'Adding...' : 'Add Role'}
        </button>
      </form>
      {error && <div className="text-red-600 text-sm">{error}</div>}

      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role Name</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {roles.map(r => (
              <tr key={r._id}>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.name}</td>
                <td className="px-4 py-3 text-right text-sm">
                  <button onClick={() => handleDelete(r._id)} className="text-red-500 hover:text-red-700">Delete</button>
                </td>
              </tr>
            ))}
            {roles.length === 0 && (
              <tr>
                <td colSpan="2" className="px-4 py-4 text-center text-gray-500 text-sm">No custom roles found. You can still use the core roles.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CustomRoles;
